/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import { m11ResearchStudyResourceChanged, selectM11ResearchStudyResource } from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import jsonEquals from "../../functions/jsonEquals";
import StringEntry from "../../datatypeComponents/StringEntry";
import { DateEntry } from "../../datatypeComponents/PeriodEntry";
import CodeableConceptEntry from "../../datatypeComponents/CodeableConceptEntry";
import ArrayEntry from "../../datatypeComponents/ArrayEntry";
import saveM11Changes from "../../functions/saveM11ChangesAPI";
import { selectM11ResearchStudyFoi } from "../../slices/m11StepCount/m11StepCountSlice";
import { validateStep } from "../../functions/validateM11ReportAPI";

const researchStudyPhaseCodeableConceptSet = [
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C54721", display: "Early Phase 1" },
            { system: "https://fevir.net/sevco", code: "SEVCO:01031", display: "exploratory investigational new drug study" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C15600", display: "Phase 1" },
            { system: "https://fevir.net/sevco", code: "SEVCO:01030", display: "phase 1 trial" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C15693", display: "Phase 1/Phase 2" },
            { system: "https://fevir.net/sevco", code: "SEVCO:01032", display: "phase 1/phase 2 trial" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C15601", display: "Phase 2" },
            { system: "https://fevir.net/sevco", code: "SEVCO:01033", display: "phase 2 trial" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C15694", display: "Phase 2/Phase 3" },
            { system: "https://fevir.net/sevco", code: "SEVCO:01034", display: "phase 2/phase 3 trial" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C15602", display: "Phase 3" },
            { system: "https://fevir.net/sevco", code: "SEVCO:01035", display: "phase 3 trial" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C217025", display: "Phase 3/Phase 4" },
            { system: "https://fevir.net/sevco", code: "SEVCO:01099", display: "phase 3/phase 4 trial" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C15603", display: "Phase 4" },
            { system: "https://fevir.net/sevco", code: "SEVCO:01036", display: "post-marketing study" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C198366", display: "Phase 1/Phase 2/Phase 3" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C198367", display: "Phase 1/Phase 3" }
        ]
    },
    {
        "coding": [
            { system: "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl", code: "C217024", display: "Phase 2/Phase 3/Phase 4" }
        ]
    }
];

const saveTitleChanges = async (dispatch, apiToken, m11ResearchStudyFoi,
    titlesState, setAlertModalState, setStartingTitlesState) => {
    let changesStates = {
        titlesState: titlesState
    };
    let response = await saveM11Changes(apiToken, 3, m11ResearchStudyFoi, changesStates);
    if (response?.success) {
        if (response.m11ResearchStudyResource) {
            dispatch(m11ResearchStudyResourceChanged(response.m11ResearchStudyResource));
        }
        setStartingTitlesState(JSON.parse(JSON.stringify(titlesState)));
    } else {
        if (response.error) {
            alert(response.error);
        }
    }
};

const M11Step003TitlePageData = forwardRef(({ theM11ResearchStudyFoi, setAlertModalState }, ref) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyResource = useSelector(selectM11ResearchStudyResource);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [titlesState, setTitlesState] = useState({
        "fullTitle": "", "trialAcronym": "", "shortTitle": "",
        "version": "", "date": "", "phase": "",
        "productCode": [], "productProprietaryName": [], "productNonproprietaryName": []
    });
    const [startingTitlesState, setStartingTitlesState] = useState({
        "fullTitle": "", "trialAcronym": "", "shortTitle": "",
        "version": "", "date": "", "phase": "",
        "productCode": [], "productProprietaryName": [], "productNonproprietaryName": []
    });

    useImperativeHandle(ref, () => ({
        unsavedChanges: (m11ResearchStudyResource && !jsonEquals(titlesState, startingTitlesState)),
        async saveAllChanges() {
            await saveTitleChanges(dispatch, apiToken, m11ResearchStudyFoi,
                titlesState, setAlertModalState, setStartingTitlesState);
        }
    }));

    useEffect(() => {
        if (m11ResearchStudyResource) {
            let startingTitleSet = {
                "fullTitle": m11ResearchStudyResource.title || "",
                "trialAcronym": "", "shortTitle": "", "phase": ""
            };
            if (m11ResearchStudyResource.label?.length > 0) {
                for (const label of m11ResearchStudyResource.label) {
                    if (label.type?.coding?.[0]?.code === "acronym" && label.value) {
                        startingTitleSet.trialAcronym = label.value;
                        break;
                    }
                }
                for (const label of m11ResearchStudyResource.label) {
                    if (label.type?.coding?.[0]?.code === "short-title" && label.value) {
                        startingTitleSet.shortTitle = label.value;
                        break;
                    }
                }
            }
            if (m11ResearchStudyResource.phase) {
                startingTitleSet.phase = m11ResearchStudyResource.phase;
            }
            if (m11ResearchStudyResource.version) {
                startingTitleSet.version = m11ResearchStudyResource.version;
            }
            if (m11ResearchStudyResource.date) {
                startingTitleSet.date = m11ResearchStudyResource.date;
            }
            let productCodeList = [];
            let productProprietaryNameList = [];
            let productNonproprietaryNameList = [];
            if (m11ResearchStudyResource.focus?.length) {
                for (const focus of m11ResearchStudyResource.focus) {
                    if (focus.concept?.coding?.[0]?.system === "ForUseWithM11ReportAuthoringTool" &&
                        focus.concept.text) {
                        if (focus.concept.coding[0].display === "Sponsor's Product Code") {
                            productCodeList.push(focus.concept.text);
                        } else if (focus.concept.coding[0].display === "Product Proprietary Name") {
                            productProprietaryNameList.push(focus.concept.text);
                        } else if (focus.concept.coding[0].display === "Product Nonproprietary Name") {
                            productNonproprietaryNameList.push(focus.concept.text);
                        }
                    }
                }
            }
            startingTitleSet.productCode = productCodeList;
            startingTitleSet.productProprietaryName = productProprietaryNameList;
            startingTitleSet.productNonproprietaryName = productNonproprietaryNameList;
            startingTitleSet.loaded = true;
            setTitlesState(startingTitleSet);
            setStartingTitlesState(startingTitleSet);
        }
    }, [m11ResearchStudyResource]);

    return <div>
        <div className="h3Style h3EntryLabel" style={{ marginBottom: "0px" }}>
            Enter Titles, Products, and Phase for M11ResearchStudy.<span> This will be used for the M11 Report Title Page.</span>
        </div>
        {titlesState.loaded && <>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Edit the </span>Full Title<span> if desired.</span>
            </div>
            <StringEntry elementName='fullTitle'
                required={true}
                fieldLabel='Full Title'
                startingValue={titlesState.fullTitle}
                setResourceState={setTitlesState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Add </span>Trial Acronym<span> if desired.</span>
            </div>
             <div style={{ maxWidth: "600px" }}>
            <StringEntry elementName='trialAcronym'
                fieldLabel='Trial Acronym'
                startingValue={titlesState.trialAcronym}
                setResourceState={setTitlesState} />
                </div>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Add </span>Short Title<span> if desired.</span>
            </div>
            <StringEntry elementName='shortTitle'
                fieldLabel='Short Title'
                startingValue={titlesState.shortTitle}
                setResourceState={setTitlesState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Add </span>Version Number<span> if desired.</span>
            </div>            
             <div style={{ maxWidth: "160px" }}>
            <StringEntry elementName='version'
                fieldLabel='Version Number'
                startingValue={titlesState.version}
                setResourceState={setTitlesState} />
                </div>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Add </span>Version Date<span> if desired.</span>
            </div>
            <DateEntry elementName='date'
                fieldLabel='Version Date'
                startingValue={titlesState.date}
                setResourceState={setTitlesState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Select the </span>Trial Phase<span>.</span>
            </div>
            <CodeableConceptEntry elementName='phase' fieldLabel=''
                noWriteIn
                codeableConceptLevelValueSet={researchStudyPhaseCodeableConceptSet}
                startingValue={titlesState.phase} setResourceState={setTitlesState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter the </span>Sponsor's Investigational Product Code(s)<span>. (optional)</span>
            </div>
            {!jsonEquals(titlesState, {
                "fullTitle": "", "trialAcronym": "", "shortTitle": "", "phase": "",
                "productCode": [], "productProprietaryName": [], "productNonproprietaryName": []
            }) &&
                <ArrayEntry datatype='string' elementName='productCode'
                    fieldLabel='Product Code'
                    doNotShowFieldLabel={true}
                    startingValue={titlesState.productCode}
                    setResourceState={setTitlesState} />
            }
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter the </span>Investigational Product Proprietary Name(s)<span>. (optional)</span>
            </div>
            {!jsonEquals(titlesState, {
                "fullTitle": "", "trialAcronym": "", "shortTitle": "", "phase": "",
                "productCode": [], "productProprietaryName": [], "productNonproprietaryName": []
            }) &&
                <ArrayEntry datatype='string' elementName='productProprietaryName'
                    fieldLabel='Product Proprietary Name'
                    doNotShowFieldLabel={true}
                    startingValue={titlesState.productProprietaryName}
                    setResourceState={setTitlesState} />
            }
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter the </span>Investigational Product Nonproprietary Name(s)<span>. (optional)</span>
            </div>
            {!jsonEquals(titlesState, {
                "fullTitle": "", "trialAcronym": "", "shortTitle": "", "phase": "",
                "productCode": [], "productProprietaryName": [], "productNonproprietaryName": []
            }) &&
                <ArrayEntry datatype='string' elementName='productNonproprietaryName'
                    fieldLabel='Product Nonproprietary Name'
                    doNotShowFieldLabel={true}
                    startingValue={titlesState.productNonproprietaryName}
                    setResourceState={setTitlesState} />
            }
        </>}
    </div>
});

const M11Step003TitlePageDataHelperText = <div>
    <h3>Enter Titles, Products, and Phase for M11ResearchStudy</h3>
    <div style={{ textAlign: "left" }}>
        <p>Edit the Full Title if desired. This is required for the M11 Report Title Page. The protocol should have a descriptive title that identifies the scientific aspects of the trial sufficiently to ensure it is immediately evident what the trial is investigating and on whom, and to allow retrieval from literature or internet searches.</p>
        <p>Add Trial Acronym if desired. If present, this will be used for the M11 Report Title Page. This is an acronym or abbreviation used publicly to identify the clinical trial.</p>
        <p>Add Short Title if desired. If present, this will be used for the M11 Report Title Page. Short title should convey in plain language what the trial is about and should be suitable for use as “Brief Title” or “Title in Plain Language” in global clinical trial registries. It can also be suitable for use with informed consent forms (ICFs) and ethics committee submissions.</p>
        <p>Add Version Number if desired. If present, this will be used for the M11 Report Title Page.</p>
        <p>Add Version Date if desired. If present, this will be used for the M11 Report Title Page.</p>
        <p>Select the Trial Phase. This is required for the M11 Report Title Page. For trials combining investigational drugs or vaccines with devices, classify according to the phase of drug development.</p>
        <p>Enter the Sponsor's Investigational Product Code(s) (optional) -- if used, enter the sponsor’s unique identifier for investigational product(s) in the trial.</p>
        <p>Enter the Investigational Product Proprietary Name(s) (optional) -- if used, enter the proprietary name for investigational product(s) in the trial. Leave blank if proprietary name not yet established.</p>
        <p>Enter the Investigational Product Nonproprietary Name(s) (optional) -- if used, enter the nonproprietary name for investigational product(s) in the trial. Leave blank if nonproprietary name has not yet been assigned.</p>
        <p>Click the 'Save All Changes' button.</p>
    </div>
</div>

// eslint-disable-next-line no-empty-pattern
const M11Step003ValidationCheck = ({ }) => {

    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [validationCheckState, setValidationCheckState] = useState({});

    useEffect(() => {
        if (m11ResearchStudyFoi) {
            validateStep(apiToken, 3, m11ResearchStudyFoi,
                setValidationCheckState)
        }
    }, [m11ResearchStudyFoi]);

    return <div>{validationCheckState?.success && <div>
        <h4>Step 3 (Enter Titles, Products, and Phase for M11ResearchStudy) {validationCheckState.step03numberOfChecks} issues checked. {validationCheckState.step03numberPassed} passed. {validationCheckState.step03numberFailed} failed.</h4>
        <p>Full Title has data stored in M11ResearchStudy Resource: {validationCheckState.resultCheckTitle}</p>
        <p>If Version Date is present, it is stored in YYYY-MM-DD date format: {validationCheckState.resultCheckDateFormat}</p>
        <p>Trial Phase is present and matches an allowed value: {validationCheckState.resultCheckPhase}</p>
        <p>At least one value is present for Sponsor's Investigational Product Code or Nonproprietary Name: {validationCheckState.resultCheckProductCodeOrNonproprietaryName}</p>
    </div>}  </div>
}

export default M11Step003TitlePageData;
export { M11Step003TitlePageDataHelperText, M11Step003ValidationCheck };